import { IconButton } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import TextSnippetOutlinedIcon from '@mui/icons-material/TextSnippetOutlined';
import EditIcon from '@mui/icons-material/Edit';
import { Badge, Button as ButtonMui, Tooltip } from '@mui/material';
import { useEffect, useState } from 'react';
import { IConversationViewModel, ISolicitationViewModel } from 'viewModels';
import {
  IAddParticipantConversationInputModel,
  IConversationForSolicitationInputModel,
  IUpdateSolicitationInputModel,
} from 'inputModels';
import { serialize } from 'object-to-formdata';
import { useTheme } from 'styled-components';
import { ReactComponent as CloseIcon } from '../../../assets/icons/IconCloseCircle.svg';
import { ReactComponent as ChatIcon } from '../../../assets/icons/chat-balloon-icon.svg';
import { SolicitationController } from '../../../controller/SolicitationController';
import {
  labelServiceStatus,
  ModalPaymentType,
  ProfileType,
  RequestsSubmittedStatus,
  ServiceStatus,
  TypeUser,
} from '../../../types/enums';
import { Button } from '../../Buttons/Button';
import { HistoricService } from '../../HistoricService';
import { EditHistory } from '../../EditHistory';
import { ModalSolicitationRefuse } from '../../Modals/ModalSolicitationRefuse/index';
import { ModalStatusPayment } from '../../Modals/ModalStatusPayment/index';
import { OptionsEventSolicitations } from '../../OptionsEventSolicitation';
import { SolicitationContentData } from '../../SolicitationContentData';
import { TradingSolicitation } from '../../TradingSolicitation';
import { Container, Content, Header } from './styles';
import { useApp, useAuth } from '../../../context';
import { ModalAlertConfirmation } from '../../Modals/ModalAlertConfirmation';
import { useSolicitation } from '../../../context/solicitation';
import { useAlert } from '../../../hooks/useAlert';
import { SolicitationMapLocation } from '../../SolicitationMapLocation';
import { useConversation } from '../../../context/conversation';
import { ModalChat } from '../../Modals/ModalChat';
import ConversationController from '../../../controller/ConversationController';
import { ModalCancelSolicitation } from '../../Modals/ModalCancelSolicitation';
import { useNavigate } from 'react-router-dom';
//import { SolicitationMapLocation } from '../SolicitationMapLocation';

interface Props {
  solicitationParam: ISolicitationViewModel;
  isBorder?: boolean;
  zIndex?: number;
  isTutorial?: boolean;
}

export function CardSolicitationDetails({
  solicitationParam,
  isBorder,
  zIndex,
  isTutorial = false,
}: Props) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [solicitation, setSolicitation] = useState<ISolicitationViewModel>(solicitationParam);
  const { setLoading } = useApp();
  const { conversations, setConversations } = useConversation();
  const alert = useAlert();
  const { setSolicitations } = useSolicitation();
  const [isOpenChat, setIsOpenChat] = useState(false);
  const [conversationIdSelected, setConversationIdSelected] = useState<string>();
  const [isOpenInformation, setIsOpenInformation] = useState(false);
  const [isOpenHistoric, setIsOpenHistoric] = useState(false);
  const [isOpenHistoricCanceledSolicitation, setIsOpenHistoricCanceledSolicitation] =
    useState(false);
  const [isOpenModalRefuse, setIsOpenModalRefuse] = useState(false);
  const [isOpenModalStatusPayment, setIsOpenModalStatusPayment] = useState(false);
  const [isOpenMapModal, setIsOpenMapModal] = useState(false);
  const [isOpenModalCancelSolicitation, setIsOpenModalCancelSolicitation] = useState(false);
  const [isOpenModalApproverConfirm, setIsOpenModalApproverConfirm] = useState(false);
  const { colors } = useTheme();
  const handleClickOpenHistoricCanceledSolicitation = () => {
    setIsOpenHistoricCanceledSolicitation(!isOpenHistoricCanceledSolicitation);
  };

  const isAuthor = solicitation.createdBy?.id === user.id;
  const isCorrespondent = solicitation.correspondent?.id === user.id || 
    solicitation.requestsSubmitted?.some(
      (r) => r.userId === user.id && r.status === RequestsSubmittedStatus.ACCEPT,
    );
  const canEdit = isAuthor; // Status check removed - all solicitations can now be edited by author
  
  const handleEditSolicitation = () => {
    if (isTutorial) return;
    if (!canEdit) return;
    navigate(`/solicitation/${solicitation.id}/edit`);
  };

  const handleApproveSolicitation = () => {
    if (isTutorial) return;
    if (!solicitation.id) return;
    setLoading(true);

    const update: IUpdateSolicitationInputModel = {
      solicitationId: solicitation.id,
      status: ServiceStatus.IN_APPROVAL_REQUESTER,
    };
    const formData = serialize(update, { indices: true });

    SolicitationController.updateStatus(formData)
      .then(() => {
        setSolicitations((prev) => {
          const idxSolicitation = prev.findIndex((s) => s.id === solicitation.id);
          const newSolicitations = [...prev];
          if (idxSolicitation !== -1) {
            newSolicitations[idxSolicitation].status = update.status;
          }
          return newSolicitations;
        });
        alert.show({ type: 'success', message: 'Solicitação aprovada com sucesso!' });
      })
      .catch(() => {
        alert.show({ type: 'error', message: 'Erro ao aprovar solicitação!' });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleCreateConversationForSolicitation = async (
    solicitationSelected: ISolicitationViewModel,
  ) => {
    if (isTutorial) return;
    if (!user.id) return;
    if (!solicitationSelected.correspondent?.id) return;
    if (!solicitationSelected.id) return;
    const conversation: IConversationForSolicitationInputModel = {
      solicitationId: solicitationSelected.id,
      participants: [user.id, solicitationSelected.correspondent.id!],
    };
    await ConversationController.createConversationForSolicitation(conversation).then(
      (response) => {
        setConversationIdSelected(response);
        if (!conversations.some((c) => c.id === response)) {
          const newConversation: IConversationViewModel = {
            isAssistantChat: false,
            id: response,
            messages: [],
            participants: [
              {
                id: user.id,
                firstName: user.firstName,
                lastName: user.lastName,
                profilePictureId: user.profilePictureId,
              },
              {
                id: solicitationSelected.correspondent?.id!,
                firstName: solicitationSelected.correspondent?.firstName,
                lastName: solicitationSelected.correspondent?.lastName,
                profilePictureId: solicitationSelected.correspondent?.profilePictureId,
              },
            ],
          };
          setConversations([...conversations, newConversation]);
        }
        setIsOpenChat(true);
        setSolicitations((prev) => {
          const idxSolicitation = prev.findIndex((s) => s.id === solicitation.id);
          const newSolicitations = [...prev];
          if (idxSolicitation !== -1) {
            newSolicitations[idxSolicitation].conversationId = response;
          }
          return newSolicitations;
        });
      },
    );
  };

  const handleOpenChat = (conversation: IConversationViewModel) => {
    if (isTutorial) return;
    if (!user.id) return;
    if (!conversation.id) return;
    const userIsParticipant = conversation.participants.some((p) => p.id === user.id);
    if (userIsParticipant) {
      setIsOpenChat(true);
      setConversationIdSelected(conversation.id);
    } else {
      const addParticipant: IAddParticipantConversationInputModel = {
        conversationId: conversation.id,
        participantIds: [user.id],
      };
      ConversationController.addParticipants(addParticipant).then(() => {
        setConversations((prev) => {
          const idxConversation = prev.findIndex((c) => c.id === conversation.id);
          const newConversations = [...prev];
          if (idxConversation !== -1) {
            newConversations[idxConversation].participants.push({
              id: user.id,
              firstName: user.firstName,
              lastName: user.lastName,
              profilePictureId: user.profilePictureId,
            });
          }
          return newConversations;
        });
        setIsOpenChat(true);
        setConversationIdSelected(conversation.id);
      });
    }
  };

  const showIconChat = (solicitationSelected: ISolicitationViewModel) => {
    if (!user.id) return null;
    if (!solicitationSelected.correspondent?.id) return null;
    if (!solicitation.conversationId) {
      return (
        <IconButton
          onClick={() => {
            handleCreateConversationForSolicitation(solicitationSelected);
          }}>
          <ChatIcon fill={colors.tertiary_500} width="3rem" />
        </IconButton>
      );
    }
    const conversation = conversations.find((c) => c.id === solicitationSelected.conversationId);
    if (!conversation) return null;
    const counterMessage = conversation.messages.flatMap(
      (m) => m.infos?.filter((i) => i.createdById === user.id && !i.isReaded) || [],
    ).length;

    return (
      <IconButton
        onClick={() => {
          handleOpenChat(conversation);
        }}>
        <Badge
          badgeContent={counterMessage}
          sx={{
            '& .MuiBadge-badge': {
              backgroundColor: colors.error_500,
              color: colors.background_white,
            },
          }}>
          <ChatIcon fill={colors.tertiary_500} width="3rem" />
        </Badge>
      </IconButton>
    );
  };

  const handleOpenMoreInformationSolicitation = async (
    solicitationLoaded: ISolicitationViewModel,
  ) => {
    if (isOpenInformation) {
      setIsOpenInformation(false);
      return;
    }
    console.log(solicitationLoaded);
    console.log(solicitationLoaded.dateLimit);
    if (!solicitationLoaded.isSolicitationFullDataLoaded) {
      setLoading(true);
      await SolicitationController.getById(solicitationLoaded.id)
        .then((el) => {
          setSolicitations((prev) => {
            const idxSolicitation = prev.findIndex((s) => s.id === el.id);
            const newSolicitations = [...prev];
            if (idxSolicitation !== -1) {
              newSolicitations[idxSolicitation] = el;
            }
            return newSolicitations;
          });
          setSolicitation(el);
          setIsOpenInformation(true);
        })
        .catch(() => {
          alert.show({ type: 'error', message: 'Erro ao abrir mais detalhes!' });
        })
        .finally(() => {
          setLoading(false);
        });
    }
    setIsOpenInformation(true);
  };

  useEffect(() => {
    if (isTutorial) {
      setIsOpenInformation(true);
    }
  }, [isTutorial]);

  useEffect(() => {
    setSolicitation(solicitationParam);
  }, [solicitationParam]);

  return (
    <Container status={solicitation.status} isBorder={isBorder}>
      <Header status={solicitation.status}>
        {!isOpenInformation && solicitation.status !== ServiceStatus.CONCLUED ? (
          <div className="container-header-info">
            <div className="icon-title-status">
              <TextSnippetOutlinedIcon className="icon-header" />
              <div className="containerGrid">
                <div className="containerStatus">
                  {Object.keys(labelServiceStatus)
                    .filter((s) => Number(s) === Number(solicitation.status))
                    .map((status) => (
                      <p
                        id="solicitation-status"
                        className={
                          solicitation.status === ServiceStatus.CANCELED
                            ? 'statusCard canceled'
                            : solicitation.status === ServiceStatus.CONCLUED
                            ? 'statusCard conclused'
                            : solicitation.status === ServiceStatus.PENDING
                            ? 'statusCard pending'
                            : solicitation.status === ServiceStatus.UNPAID
                            ? 'statusCard unpaid'
                            : 'statusCard'
                        }
                        key={status}>
                        Status: {labelServiceStatus[status as unknown as ServiceStatus]}
                      </p>
                    ))}
                  <p id="protocol-number" className="protocol">
                    Número de Protocolo: {solicitation.protocolNumber}
                  </p>
                </div>
                <p
                  className={
                    solicitation.status === ServiceStatus.CANCELED ? 'title canceled' : 'title'
                  }>
                  {solicitation?.correspondence?.name}
                </p>
                <p className="createdAt">
                  Criada em:{' '}
                  <span>
                    {new Date(solicitation.createdAt).toLocaleDateString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </p>
              </div>
            </div>
            <div className="deadline">
              <p>{solicitation?.specialty?.name}</p>
              {solicitation.status === ServiceStatus.IN_PROGRESS &&
                solicitation.correspondent?.id &&
                showIconChat(solicitation)}
              <div className="service-date">
                <p className="title">
                  {solicitation.status !== ServiceStatus.CANCELED
                    ? 'Prazo de Cumprimento'
                    : 'Cancelado em'}
                </p>
                <p>
                  {new Date(solicitation.dateLimit).toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="container-header">
            <TextSnippetOutlinedIcon className="icon-header" />
            <div className="containerGrid">
              <div className="containerStatus">
                {Object.keys(labelServiceStatus)
                  .filter((s) => Number(s) === Number(solicitation.status))
                  .map((status) => (
                    <p
                      id="solicitation-status"
                      className={
                        solicitation.status === ServiceStatus.CANCELED
                          ? 'statusCard canceled'
                          : solicitation.status === ServiceStatus.CONCLUED
                          ? 'statusCard conclused'
                          : solicitation.status === ServiceStatus.PENDING
                          ? 'statusCard pending'
                          : solicitation.status === ServiceStatus.UNPAID
                          ? 'statusCard unpaid'
                          : 'statusCard'
                      }
                      key={status}>
                      Status: {labelServiceStatus[status as unknown as ServiceStatus]}
                    </p>
                  ))}
                <p id="protocol-number" className="protocol">
                  Número de Protocolo: {solicitation.protocolNumber}
                </p>
              </div>
              <p className="title">{solicitation?.correspondence?.name}</p>
              {solicitation.status === ServiceStatus.CONCLUED && !isOpenInformation && (
                <p className="createdAt">
                  Criada em:{' '}
                  <span>
                    {new Date(solicitation.createdAt).toLocaleDateString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}{' '}
                  </span>
                </p>
              )}
            </div>
          </div>
        )}
        <div className="containerButton">
          {isAuthor && (
            <IconButton
              onClick={handleEditSolicitation}
              disabled={!canEdit}
              style={{ marginRight: '0.5rem' }}>
              <EditIcon />
            </IconButton>
          )}
          <IconButton onClick={() => handleOpenMoreInformationSolicitation(solicitation)}>
            {isOpenInformation ? <KeyboardDoubleArrowUpIcon /> : <KeyboardDoubleArrowDownIcon />}
          </IconButton>
        </div>
      </Header>
      {isOpenInformation && (
        <>
          <Content isOpenHistory={isOpenHistoric}>
            {![ServiceStatus.CONCLUED, ServiceStatus.CANCELED, ServiceStatus.UNPAID].includes(
              solicitation.status,
            ) && (
              <>
                <div className="contentOptionsEventSolicitations" id="solicitation-modal-actions">
                  <OptionsEventSolicitations
                    isDemand={false}
                    solicitation={solicitation}
                    zIndex={zIndex}
                    handleChangeHistoricVisible={(e) => setIsOpenHistoric(e)}
                    profileType={user.collaboratorInfo?.profileType}
                  />
                </div>
                <div className="contentItensSolicitations" id="solicitation-modal-details">
                  <SolicitationContentData
                    solicitation={solicitation}
                    isVisible={!isOpenHistoric}
                  />
                  <HistoricService service={solicitation} isVisible={isOpenHistoric} />
                  {isCorrespondent && !isAuthor && !isOpenHistoric && (
                    <EditHistory solicitation={solicitation} />
                  )}
                  <TradingSolicitation solicitation={solicitation} isVisible={!isOpenHistoric} />
                </div>
              </>
            )}
          </Content>
          {![ServiceStatus.CANCELED, ServiceStatus.CONCLUED, ServiceStatus.UNPAID].includes(
            solicitation.status,
          ) && (
            <div id="solicitation-modal-botton-actions" className="buttons">
              <div className="buttons-demand">
                {![
                  ServiceStatus.IN_APPROVAL_REQUESTER,
                  ServiceStatus.IN_APPROVAL_APPROVER,
                ].includes(solicitation.status) &&
                  (user.type === TypeUser.COMPANY ||
                    user.collaboratorInfo?.profileType === ProfileType.APPROVER) && (
                    <Button
                      text="Cancelar"
                      dimensions={{ width: '10rem', height: '2rem' }}
                      color="text_gray"
                      backgroundHoverColor="transparent"
                      border={{ color: 'transparent', width: 1 }}
                      textHoverColor="text"
                      style={{ textDecoration: 'underline' }}
                      onClick={() => setIsOpenModalCancelSolicitation(true)}
                    />
                  )}
                {/* {solicitation.status === ServiceStatus.IN_PROGRESS && (
                  <Button
                    text="Ver Localização"
                    dimensions={{ width: '12rem', height: '2rem' }}
                    color="background_white"
                    backgroundHoverColor="primary"
                    background="secondary"
                    border={{ color: 'transparent', width: 0 }}
                    textHoverColor="background_white"
                    onClick={() => setIsOpenMapModal(true)}
                  />
                )} */}
                {isOpenHistoric && (
                  <Button
                    text="Voltar"
                    dimensions={{ width: '10rem', height: '2rem' }}
                    color="primary"
                    backgroundHoverColor="primary"
                    border={{ color: 'primary', width: 1 }}
                    textHoverColor="background_white"
                    onClick={() => setIsOpenHistoric(false)}
                  />
                )}
                {solicitation.status === ServiceStatus.IN_APPROVAL_REQUESTER &&
                  user.id === solicitation.userId && (
                    <div
                      className={`contentButtonDemand ${
                        solicitation.status === ServiceStatus.IN_APPROVAL_REQUESTER
                          ? 'column-align'
                          : ''
                      }`}>
                      <button
                        type="button"
                        className="baseButtonDemand"
                        onClick={() => setIsOpenModalRefuse(true)}>
                        Recusar
                      </button>
                      <button
                        type="button"
                        className="baseButtonDemand"
                        onClick={() => setIsOpenModalStatusPayment(true)}>
                        Finalizar
                      </button>
                    </div>
                  )}
                {isTutorial && (
                  <div
                    className={`contentButtonDemand ${
                      solicitation.status === ServiceStatus.IN_APPROVAL_REQUESTER
                        ? 'column-align'
                        : ''
                    }`}>
                    <button type="button" className="baseButtonDemand">
                      Recusar
                    </button>
                    <button type="button" className="baseButtonDemand">
                      Finalizar
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
          {Number(user.collaboratorInfo?.profileType) === Number(ProfileType.APPROVER) && (
            <div className="buttons">
              {solicitation.status === ServiceStatus.IN_APPROVAL_APPROVER && (
                <div className="buttons-demand">
                  <Button
                    text="Recusar"
                    dimensions={{ width: '10rem', height: '2rem' }}
                    color="primary"
                    backgroundHoverColor="primary"
                    border={{ color: 'primary', width: 1 }}
                    textHoverColor="background_white"
                    onClick={() => setIsOpenModalRefuse(true)}
                  />
                  <Button
                    text="Aprovar"
                    dimensions={{ width: '12rem', height: '2rem' }}
                    color="background_white"
                    backgroundHoverColor="primary"
                    background="secondary"
                    border={{ color: 'transparent', width: 0 }}
                    textHoverColor="background_white"
                    onClick={() => setIsOpenModalApproverConfirm(true)}
                  />
                </div>
              )}
            </div>
          )}
          {[ServiceStatus.CANCELED, ServiceStatus.CONCLUED, ServiceStatus.UNPAID].includes(
            solicitation.status,
          ) && (
            <>
              <div className="containerDataSolicitation">
                <SolicitationContentData solicitation={solicitation} isVisible />
                <TradingSolicitation solicitation={solicitation} isVisible />
              </div>
              <ButtonMui
                onClick={handleClickOpenHistoricCanceledSolicitation}
                className="btnHistoric">
                {isOpenHistoricCanceledSolicitation ? (
                  <>
                    <CloseIcon /> Fechar Histórico
                  </>
                ) : (
                  <>
                    <HistoryIcon />
                    Ver Histórico
                  </>
                )}
              </ButtonMui>
              <HistoricService
                service={solicitation}
                isVisible={isOpenHistoricCanceledSolicitation}
              />
            </>
          )}
        </>
      )}
      <ModalSolicitationRefuse
        solicitationId={solicitation.id!}
        isOpen={isOpenModalRefuse}
        onRequestClose={() => setIsOpenModalRefuse(false)}
      />
      {solicitation.id && (
        <ModalStatusPayment
          isOpen={isOpenModalStatusPayment}
          onRequestClose={() => setIsOpenModalStatusPayment(false)}
          solicitationId={solicitation.id}
          selectedPayment={solicitation.payment}
          type={ModalPaymentType.FINALIZE_SOLICITATION}
        />
      )}
      <ModalCancelSolicitation
        isOpen={isOpenModalCancelSolicitation}
        onRequestClose={() => setIsOpenModalCancelSolicitation(false)}
        solicitationId={solicitation.id as string}
      />
      {solicitation.id && (
        <ModalAlertConfirmation
          isOpen={isOpenModalApproverConfirm}
          data={{
            title: 'Aprovar Solicitação',
            text: 'Deseja realmente aprovar a solicitação?',
            optionConfirm: { active: true, text: 'Sim' },
            optionCancel: { active: true, text: 'Não' },
            type: 'generic',
          }}
          onRequestClose={() => setIsOpenModalApproverConfirm(false)}
          onConfirm={handleApproveSolicitation}
        />
      )}
      {solicitation.correspondent && (
        <SolicitationMapLocation
          isOpen={isOpenMapModal}
          onClose={() => {
            setIsOpenMapModal(false);
          }}
          solicitationId={solicitation.id}
          address={solicitation.address}
          destinationName={`${user.firstName} ${user.lastName}`}
          originName={`${solicitation.correspondent.firstName} ${solicitation.correspondent.lastName}`}
          type="reciver"
        />
      )}
      {conversationIdSelected && (
        <ModalChat
          isOpen={isOpenChat}
          onRequestClose={() => setIsOpenChat(false)}
          conversationId={conversationIdSelected}
        />
      )}
    </Container>
  );
}
